import {useEffect, useState} from 'react';
import {Snackbar, Alert, Button, Collapse, IconButton, Box} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useAtomValue, useSetAtom} from 'jotai';
import {notificationQueueAtom, dismissNotificationAtom} from '@/state/notificationAtoms';
import {needsReviewCountAtom, unmatchedTicketIdsAtom, unmatchedReportsAtom} from '@/state/runTicketAtoms';
import {unexplainedTankDropCountAtom} from '@/state/tankDropAtoms';
import {useDevice} from '@/hooks/useDevice.ts';
import {BOTTOM_NAV_RESERVED_HEIGHT} from '@/components/layout/AppLayout.tsx';

const AUTO_HIDE_MS = 8000;

/**
 * Global "nudge" toast — mounted once in App.tsx (see AppInner), so it
 * works from any route/page, the same way BottomNav/TopNav's Syncing
 * chip is always visible regardless of what page you're on. Shows only
 * the front of notificationQueueAtom; closing/actioning one advances to
 * the next automatically since the queue shifts.
 *
 * Bug found 2026-08-24 (Duane): saving a second time while a toast from
 * the first save was still showing appeared to do nothing, and that
 * toast then never went away. Root cause was the missing `key` below —
 * MUI's own Snackbar docs call this out explicitly: "When displaying
 * multiple consecutive snackbars using a single parent-rendered
 * <Snackbar/>, add the key prop... Otherwise, messages might update in
 * place, and features like autoHideDuration could be affected." Without
 * it, `current` advancing from one queued notification to the next just
 * updates this same Snackbar's props in place rather than remounting it
 * — and MUI's auto-hide timer is only (re-)armed on mount (its setup
 * effect's deps — open/autoHideDuration/two stable hook refs — never
 * change across a prop-only update), so the *second* notification landed
 * with no timer running at all and sat there until manually dismissed.
 * (Doubly easy to miss when both messages read identically, e.g. two
 * "Test saved" toasts in a row — no visible change either, since nothing
 * unmounts to play an exit/enter transition.) `key={current.id}` forces
 * a fresh mount — and a fresh timer — per notification.
 */
export function NotificationSnackbar() {
   const queue = useAtomValue(notificationQueueAtom);
   const dismiss = useSetAtom(dismissNotificationAtom);
   const current = queue[0];
   // Which notification's details are expanded; keyed by id so the next
   // queued toast always starts collapsed.
   const [expandedId, setExpandedId] = useState<string | null>(null);
   // Same isPhoneOrTablet check AppLayout.tsx uses to decide whether to
   // mount BottomNav at all (default 768px breakpoint) — see the sx
   // override below, which only kicks in when there's actually a
   // BottomNav underneath to avoid overlapping.
   const device = useDevice();

   // FIXED 2026-08-29 (Duane, follow-up to the same-day total-vs-delta
   // fix): "the notification is still wrong... filter shows 5, the
   // notification says 20." The earlier fix made useRunTicketReconciliation
   // push the true CURRENT total at the moment each toast was created —
   // correct then, but `current.message` is a plain string frozen at
   // push time, and the real backlog keeps moving every 30s poll. A toast
   // that sits queued for even a couple of polls (auto-hide is 8s, but
   // the QUEUE itself never expires — see AUTO_HIDE_MS below) can read
   // as stale by the time anyone actually looks at it, since nothing
   // here ever revisits the text after pushing it. Rather than trust a
   // baked-in string, recompute the count-based notifications' message
   // fresh on every render straight from the same live atoms
   // RunTicketsPage's own chips read — so whatever's on screen can never
   // drift from reality, no matter how long it sat in the queue first.
   // Only applies to notifications tagged with `kind` (see its doc
   // comment in notificationAtoms.ts); anything else (ScanTicketButton's
   // one-off confirmations, etc.) still just shows its own `message`.
   const needsReviewCount = useAtomValue(needsReviewCountAtom);
   const unmatchedTicketIds = useAtomValue(unmatchedTicketIdsAtom);
   const unmatchedReports = useAtomValue(unmatchedReportsAtom);
   const unexplainedTankDropCount = useAtomValue(unexplainedTankDropCountAtom);

   // FIXED 2026-08-31 (Duane, "the unmatched number...doesn't always
   // have the correct number" — this specific gap found while
   // investigating that report): these two branches used to have no
   // total === 0 case at all, unlike 'unmatchedReports' right below
   // (which explicitly falls through to `null`, auto-dismissing via the
   // effect below it). A toast that sits queued long enough for its
   // count to genuinely drop to 0 before anyone sees it — the exact
   // scenario the `kind` mechanism as a whole exists for, per its own
   // doc comment in notificationAtoms.ts — used to render the literal,
   // confusing "0 run tickets are ready to review."/"0 run tickets have
   // no matching Daily Report." instead of silently advancing to
   // whatever's next in the queue.
   let liveMessage: string | null = null;
   if (current?.kind === 'needsReview') {
      liveMessage =
         needsReviewCount === 1
            ? '1 run ticket is ready to review.'
            : needsReviewCount > 1
              ? `${needsReviewCount} run tickets are ready to review.`
              : null; // count === 0, handled by the auto-dismiss effect below
   } else if (current?.kind === 'unmatchedTickets') {
      const total = unmatchedTicketIds.size;
      liveMessage =
         total === 1
            ? '1 run ticket has no matching Daily Report.'
            : total > 1
              ? `${total} run tickets have no matching Daily Report.`
              : null; // total === 0, handled by the auto-dismiss effect below
   } else if (current?.kind === 'unmatchedReports') {
      const total = unmatchedReports.length;
      liveMessage =
         total === 1
            ? `${unmatchedReports[0].wellName}'s Daily Report references ticket #${unmatchedReports[0].runTicketNumber} — no matching Run Ticket yet.`
            : total > 1
              ? `${total} Daily Reports reference a run ticket number with no matching Run Ticket.`
              : null; // total === 0, handled by the auto-dismiss effect below
   } else if (current?.kind === 'unexplainedTankDrops') {
      // Chunk 7 (2026-09-30) — see notificationAtoms.ts's own doc comment
      // on this kind and useTankDropReconciliation.ts for what pushes it.
      liveMessage =
         unexplainedTankDropCount === 1
            ? '1 tank has an unexplained gauge drop.'
            : unexplainedTankDropCount > 1
              ? `${unexplainedTankDropCount} tanks have an unexplained gauge drop.`
              : null; // count === 0, handled by the auto-dismiss effect below
   }

   // The specific thing this toast was about may have already resolved
   // (report edited, ticket added) by the time it's actually shown, since
   // it can sit queued behind other toasts or simply go unread for a
   // while. Showing "0 run tickets..." would be confusing at best — just
   // silently advance to whatever's next in the queue instead, same as a
   // normal dismiss.
   const shouldAutoDismiss = current != null && current.kind != null && liveMessage === null;
   useEffect(() => {
      if (shouldAutoDismiss && current) dismiss(current.id);
   }, [shouldAutoDismiss, current, dismiss]);

   if (!current || shouldAutoDismiss) return null;

   function handleAction() {
      current.onAction?.();
      dismiss(current.id);
   }

   return (
      <Snackbar
         key={current.id}
         open
         // A toast with details stays until dismissed — there's no reading
         // (or copying) technical text inside 8 seconds.
         autoHideDuration={current.details ? null : AUTO_HIDE_MS}
         onClose={(_e, reason) => {
            // Ignore clickaway — a nudge shouldn't vanish just because
            // the user tapped elsewhere on the page; only the timeout,
            // the action button, or the close (X) button dismiss it.
            if (reason === 'clickaway') return;
            dismiss(current.id);
         }}
         anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
         sx={{
            // FIXED 2026-09-02 (Duane, mobile): MUI's Snackbar renders at
            // zIndex: 1400, above BottomNav's AppBar (zIndex: 1100) — with
            // no offset here, the toast sat on top of the bottom nav bar
            // instead of just near it, both visually covering it and
            // winning touch hit-testing so nav taps landed on the toast
            // instead. Offsetting by BOTTOM_NAV_RESERVED_HEIGHT (same
            // constant AppLayout.tsx uses to reserve space for BottomNav
            // in page content) plus MUI's own default 8px edge gap puts
            // the toast above the bar instead of over it. Desktop/tablet
            // widths with no BottomNav keep MUI's default bottom offset.
            bottom: device.isPhoneOrTablet ? `${BOTTOM_NAV_RESERVED_HEIGHT + 8}px` : undefined,
         }}
      >
         <Alert
            severity={current.severity ?? 'info'}
            variant='filled'
            action={
               <>
                  {current.details && (
                     <Button
                        color='inherit'
                        size='small'
                        aria-expanded={expandedId === current.id}
                        onClick={() => setExpandedId(expandedId === current.id ? null : current.id)}
                     >
                        {expandedId === current.id ? 'Hide details' : 'Show details'}
                     </Button>
                  )}
                  {current.actionLabel && current.onAction && (
                     <Button color='inherit' size='small' onClick={handleAction}>
                        {current.actionLabel}
                     </Button>
                  )}
                  {/* FIXED 2026-09-02 (Duane, mobile): a toast with an
                      action button (the three run-ticket reconciliation
                      "Review" toasts — see useRunTicketReconciliation.ts)
                      used to have NO way to dismiss it early — MUI's Alert
                      only auto-renders its default close icon when no
                      custom `action` is passed, so passing one here
                      suppressed it entirely. With 3 of these possibly
                      queued at 8s auto-hide each, that's up to ~24s stuck
                      waiting (or having to act on the notification just to
                      get rid of it) before AUTO_HIDE_MS above self-clears
                      it. Rendering our own close icon here always — instead
                      of relying on Alert's implicit onClose-driven default —
                      keeps every toast, action or not, dismissible with one
                      tap. */}
                  <IconButton
                     size='small'
                     color='inherit'
                     aria-label='Dismiss notification'
                     onClick={() => dismiss(current.id)}
                  >
                     <CloseIcon fontSize='small' />
                  </IconButton>
               </>
            }
            sx={{width: '100%'}}
         >
            {liveMessage ?? current.message}
            {current.details && (
               <Collapse in={expandedId === current.id} unmountOnExit>
                  <Box
                     component='pre'
                     sx={{
                        m: 0,
                        mt: 1,
                        maxHeight: 160,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        userSelect: 'text',
                     }}
                  >
                     {current.details}
                  </Box>
               </Collapse>
            )}
         </Alert>
      </Snackbar>
   );
}
